
const formatDate = (date, locale = 'nl-NL') => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }).replace(/\//g, '-');
};

/**
 * Get recipient name from recipient object or string
 */
const getRecipientName = (recipient) => {
  if (!recipient) return '';
  if (typeof recipient === 'string') {
    return recipient;
  }
  if (recipient.businessName) {
    return recipient.businessName;
  }
  const firstName = recipient.firstName || '';
  const middleName = recipient.middleName || '';
  const lastName = recipient.lastName || '';
  return `${firstName} ${middleName} ${lastName}`.trim() || recipient.name || recipient.email || '';
};

/**
 * Get recipient email
 */
const getRecipientEmail = (recipient) => {
  if (!recipient) return '';
  if (typeof recipient === 'string') {
    return recipient;
  }
  return recipient.email || recipient.contacts?.[0]?.email || '';
};

/**
 * Get recipient business name
 */
const getRecipientBusinessName = (recipient) => {
  if (!recipient) return '';
  if (typeof recipient === 'object' && recipient.businessName) {
    return recipient.businessName;
  }
  return '';
};

/**
 * Get sender name
 */
const getFromName = (from) => {
  if (!from) return '';
  if (from.businessName && from.businessName.trim()) {
    return from.businessName;
  }
  const firstName = from.firstName || '';
  const middleName = from.middleName || '';
  const lastName = from.lastName || '';
  return `${firstName} ${middleName} ${lastName}`.trim() || from.name || '';
};

/**
 * Get sender email
 */
const getFromEmail = (from) => {
  if (!from) return '';
  return from.email || from.contacts?.[0]?.email || '';
};

/**
 * Get case file name
 */
const getCaseFileName = (selectedCase) => {
  if (!selectedCase) return '';
  return selectedCase.fileName || selectedCase.name || '';
};

/**
 * Get case file number
 */
const getCaseFileNo = (selectedCase) => {
  if (!selectedCase) return '';
  return selectedCase.fileNo || '';
};

/**
 * Get accident date
 */
const getAccidentDate = (selectedCase) => {
  if (!selectedCase || !selectedCase.dateOfAccident) return '';
  return formatDate(selectedCase.dateOfAccident);
};

/**
 * Get current date
 */
const getCurrentDate = () => {
  return formatDate(new Date());
};

/**
 * Get recipient first name
 */
const getRecipientFirstName = (recipient) => {
  if (!recipient || typeof recipient === 'string') return '';
  return recipient.firstName || '';
};

/**
 * Get recipient middle name
 */
const getRecipientMiddleName = (recipient) => {
  if (!recipient || typeof recipient === 'string') return '';
  return recipient.middleName || '';
};

/**
 * Get recipient last name
 */
const getRecipientLastName = (recipient) => {
  if (!recipient || typeof recipient === 'string') return '';
  return recipient.lastName || '';
};

/**
 * Get recipient phone
 */
const getRecipientPhone = (recipient) => {
  if (!recipient || typeof recipient === 'string') return '';
  return recipient.contacts?.[0]?.phone || recipient.phone || '';
};

/**
 * Get recipient position
 */
const getRecipientPosition = (recipient) => {
  if (!recipient || typeof recipient === 'string') return '';
  return recipient.contacts?.[0]?.position || '';
};

/**
 * Get recipient website
 */
const getRecipientWebsite = (recipient) => {
  if (!recipient || typeof recipient === 'string') return '';
  return recipient.website || '';
};

/**
 * Get recipient KVK (Chamber of Commerce)
 */
const getRecipientKvk = (recipient) => {
  if (!recipient || typeof recipient === 'string') return '';
  return recipient.kvk || '';
};

/**
 * Get recipient unique number
 */
const getRecipientUniqueNumber = (recipient) => {
  if (!recipient || typeof recipient === 'string') return '';
  return recipient.uniqueNumber ? String(recipient.uniqueNumber) : '';
};

/**
 * Get recipient date of birth
 */
const getRecipientDateOfBirth = (recipient) => {
  if (!recipient || typeof recipient === 'string') return '';
  return recipient.dateOfBirth ? formatDate(recipient.dateOfBirth) : '';
};

/**
 * Get recipient sex/gender
 */
const getRecipientSex = (recipient) => {
  if (!recipient || typeof recipient === 'string') return '';
  return recipient.sex || '';
};

/**
 * Get recipient BSN
 */
const getRecipientBsn = (recipient) => {
  if (!recipient || typeof recipient === 'string') return '';
  return recipient.bsn || '';
};

/**
 * Get recipient marital status
 */
const getRecipientMaritalStatus = (recipient) => {
  if (!recipient || typeof recipient === 'string') return '';
  return recipient.burgerlijkeStaat || '';
};

/**
 * Get recipient address
 */
const getRecipientAddress = (recipient) => {
  if (!recipient || typeof recipient === 'string') return '';
  return recipient.addresses?.[0]?.address || '';
};

/**
 * Get recipient postcode
 */
const getRecipientPostcode = (recipient) => {
  if (!recipient || typeof recipient === 'string') return '';
  return recipient.addresses?.[0]?.postcode || '';
};

/**
 * Get recipient place/city
 */
const getRecipientPlace = (recipient) => {
  if (!recipient || typeof recipient === 'string') return '';
  return recipient.addresses?.[0]?.place || '';
};

/**
 * Get recipient country
 */
const getRecipientCountry = (recipient) => {
  if (!recipient || typeof recipient === 'string') return '';
  return recipient.addresses?.[0]?.country || '';
};

/**
 * Get sender first name
 */
const getFromFirstName = (from) => {
  if (!from) return '';
  return from.firstName || '';
};

/**
 * Get sender middle name
 */
const getFromMiddleName = (from) => {
  if (!from) return '';
  return from.middleName || '';
};

/**
 * Get sender last name
 */
const getFromLastName = (from) => {
  if (!from) return '';
  return from.lastName || '';
};

/**
 * Get sender phone
 */
const getFromPhone = (from) => {
  if (!from) return '';
  return from.contacts?.[0]?.phone || from.phone || '';
};

/**
 * Get sender position
 */
const getFromPosition = (from) => {
  if (!from) return '';
  return from.contacts?.[0]?.position || '';
};

/**
 * Get sender website
 */
const getFromWebsite = (from) => {
  if (!from) return '';
  return from.website || '';
};

/**
 * Get sender KVK
 */
const getFromKvk = (from) => {
  if (!from) return '';
  return from.kvk || '';
};

/**
 * Get sender unique number
 */
const getFromUniqueNumber = (from) => {
  if (!from) return '';
  return from.uniqueNumber ? String(from.uniqueNumber) : '';
};

/**
 * Get sender address
 */
const getFromAddress = (from) => {
  if (!from) return '';
  return from.addresses?.[0]?.address || '';
};

/**
 * Get sender postcode
 */
const getFromPostcode = (from) => {
  if (!from) return '';
  return from.addresses?.[0]?.postcode || '';
};

/**
 * Get sender place/city
 */
const getFromPlace = (from) => {
  if (!from) return '';
  return from.addresses?.[0]?.place || '';
};

/**
 * Get sender country
 */
const getFromCountry = (from) => {
  if (!from) return '';
  return from.addresses?.[0]?.country || '';
};

/**
 * Get case type
 */
const getCaseType = (selectedCase) => {
  if (!selectedCase) return '';
  return selectedCase.type || '';
};

/**
 * Get case status
 */
const getCaseStatus = (selectedCase) => {
  if (!selectedCase) return '';
  return selectedCase.status || '';
};

/**
 * Get case reference
 */
const getCaseReference = (selectedCase) => {
  if (!selectedCase) return '';
  return selectedCase.reference || '';
};

/**
 * Get case language
 */
const getCaseLanguage = (selectedCase) => {
  if (!selectedCase) return '';
  return selectedCase.language || '';
};

/**
 * Get case event date
 */
const getCaseEventDate = (selectedCase) => {
  if (!selectedCase || !selectedCase.eventDate) return '';
  return formatDate(selectedCase.eventDate);
};

/**
 * Get case created date
 */
const getCaseCreatedDate = (selectedCase) => {
  if (!selectedCase) return '';
  const date = selectedCase.createdOn || selectedCase.createdAt;
  return date ? formatDate(date) : '';
};

/**
 * Get case inactive date
 */
const getCaseInactiveDate = (selectedCase) => {
  if (!selectedCase || !selectedCase.inactiveDate) return '';
  return formatDate(selectedCase.inactiveDate);
};

/**
 * Get client licence plate
 */
const getClientLicencePlate = (selectedCase) => {
  if (!selectedCase) return '';
  return selectedCase.clientLicencePlate || '';
};

/**
 * Get other party licence plate
 */
const getOtherPartyLicencePlate = (selectedCase) => {
  if (!selectedCase) return '';
  return selectedCase.otherPartyLicencePlate || '';
};

/**
 * Get insurance policy number
 */
const getInsurancePolicyNumber = (selectedCase) => {
  if (!selectedCase) return '';
  return selectedCase.insurancePolicyNumber || '';
};

const normalizeTagName = (tagName) => {
  if (!tagName || typeof tagName !== 'string') return tagName;
  
  if (/[A-Z]/.test(tagName)) {
    return tagName;
  }
  
  const commonPatterns = {
    'recipientname': 'recipientName',
    'recipientemail': 'recipientEmail',
    'recipientfirstname': 'recipientFirstName',
    'recipientmiddlename': 'recipientMiddleName',
    'recipientlastname': 'recipientLastName',
    'recipientphone': 'recipientPhone',
    'recipientbusinessname': 'recipientBusinessName',
    'recipientaddress': 'recipientAddress',
    'recipientpostcode': 'recipientPostcode',
    'recipientplace': 'recipientPlace',
    'recipientcountry': 'recipientCountry',
    'fromname': 'fromName',
    'fromemail': 'fromEmail',
    'fromfirstname': 'fromFirstName',
    'frommiddlename': 'fromMiddleName',
    'fromlastname': 'fromLastName',
    'fromphone': 'fromPhone',
    'fromaddress': 'fromAddress',
    'casefilename': 'caseFileName',
    'casefileno': 'caseFileNo',
    'casename': 'caseName',
    'accidentdate': 'accidentDate',
    'currentdate': 'currentDate',
    'formatteddate': 'formattedDate',
    'casetype': 'caseType',
    'casestatus': 'caseStatus',
    'casereference': 'caseReference',
    'caselanguage': 'caseLanguage',
    'caseeventdate': 'caseEventDate',
    'casecreateddate': 'caseCreatedDate',
    'caseinactivedate': 'caseInactiveDate',
    'clientlicenceplate': 'clientLicencePlate',
    'otherpartylicenceplate': 'otherPartyLicencePlate',
    'insurancepolicynumber': 'insurancePolicyNumber',
    'opposingpartyfilenumber': 'opposingPartyFileNumber',
    'clientname': 'clientName',
    'clientemail': 'clientEmail',
    'clientbusinessname': 'clientBusinessName',
    'clientfirstname': 'clientFirstName',
    'clientmiddlename': 'clientMiddleName',
    'clientlastname': 'clientLastName',
    'clientphone': 'clientPhone',
    'clientposition': 'clientPosition',
    'clientwebsite': 'clientWebsite',
    'clientkvk': 'clientKvk',
    'clientuniquenumber': 'clientUniqueNumber',
    'clientdateofbirth': 'clientDateOfBirth',
    'clientsex': 'clientSex',
    'clientbsn': 'clientBsn',
    'clientmaritalstatus': 'clientMaritalStatus',
    'clientaddress': 'clientAddress',
    'clientpostcode': 'clientPostcode',
    'clientplace': 'clientPlace',
    'clientcountry': 'clientCountry',
  };
  
  const normalized = commonPatterns[tagName.toLowerCase()];
  if (normalized) {
    return normalized;
  }
  
  if (tagName.startsWith('client')) {
    const rest = tagName.substring(6);
    return 'client' + (rest.charAt(0).toUpperCase() + rest.slice(1));
  }
  if (tagName.startsWith('recipient')) {
    const rest = tagName.substring(9);
    return 'recipient' + (rest.charAt(0).toUpperCase() + rest.slice(1));
  } else if (tagName.startsWith('from')) {
    const rest = tagName.substring(4);
    return 'from' + (rest.charAt(0).toUpperCase() + rest.slice(1));
  } else if (tagName.startsWith('case')) {
    const rest = tagName.substring(4);
    return 'case' + (rest.charAt(0).toUpperCase() + rest.slice(1));
  }
  
  return tagName;
};

const getTagValue = (tagName, context, availableTags = []) => {
  const normalizedTagName = normalizeTagName(tagName);
  
  const {
    recipient,
    selectedCase,
    from,
    user,
    client,
    responsibleLawyer,
    treatingLawyer,
  } = context;

  let recipientObj = Array.isArray(recipient) ? recipient[0] : recipient;
  const clientObj = client;

  // Helper: find party by type
  const getPartyByType = (type) => {
    if (!selectedCase || !Array.isArray(selectedCase.parties)) return null;
    const party = selectedCase.parties.find((p) => p.type === type);
    return party?.name || null;
  };

  const opposingParty = getPartyByType('Opposite Party');
  const payingParty = getPartyByType('Paying Party');
  const advocateOpposingParty = getPartyByType('Advocate Opposing Party');
  
  // Early return for recipient-specific tags when recipient is a string
  if (typeof recipientObj === 'string') {
    if (normalizedTagName === 'recipientName' || normalizedTagName === 'recipientEmail') {
      return recipientObj;
    }
  }

  // Direct handler mapping - call functions directly with current context values
  const handlerMap = {
    // Recipient tags
    recipientName: () => getRecipientName(recipientObj),
    recipientEmail: () => getRecipientEmail(recipientObj),
    recipientBusinessName: () => getRecipientBusinessName(recipientObj),
    recipientFirstName: () => getRecipientFirstName(recipientObj),
    recipientMiddleName: () => getRecipientMiddleName(recipientObj),
    recipientLastName: () => getRecipientLastName(recipientObj),
    recipientPhone: () => getRecipientPhone(recipientObj),
    recipientPosition: () => getRecipientPosition(recipientObj),
    recipientWebsite: () => getRecipientWebsite(recipientObj),
    recipientKvk: () => getRecipientKvk(recipientObj),
    recipientUniqueNumber: () => getRecipientUniqueNumber(recipientObj),
    recipientDateOfBirth: () => getRecipientDateOfBirth(recipientObj),
    recipientSex: () => getRecipientSex(recipientObj),
    recipientBsn: () => getRecipientBsn(recipientObj),
    recipientMaritalStatus: () => getRecipientMaritalStatus(recipientObj),
    recipientAddress: () => getRecipientAddress(recipientObj),
    recipientPostcode: () => getRecipientPostcode(recipientObj),
    recipientPlace: () => getRecipientPlace(recipientObj),
    recipientCountry: () => getRecipientCountry(recipientObj),
    
    // Case tags
    caseFileName: () => getCaseFileName(selectedCase),
    caseFileNo: () => getCaseFileNo(selectedCase),
    caseName: () => selectedCase?.name || '',
    accidentDate: () => getAccidentDate(selectedCase),
    opposingPartyFileNumber: () => selectedCase?.opposingPartyFileNumber || '',
    clientLicencePlate: () => getClientLicencePlate(selectedCase),
    otherPartyLicencePlate: () => getOtherPartyLicencePlate(selectedCase),
    insurancePolicyNumber: () => getInsurancePolicyNumber(selectedCase),
    caseType: () => getCaseType(selectedCase),
    caseStatus: () => getCaseStatus(selectedCase),
    caseReference: () => getCaseReference(selectedCase),
    caseLanguage: () => getCaseLanguage(selectedCase),
    caseEventDate: () => getCaseEventDate(selectedCase),
    caseCreatedDate: () => getCaseCreatedDate(selectedCase),
    caseInactiveDate: () => getCaseInactiveDate(selectedCase),
    
    // Client (case client) tags
    clientName: () => getRecipientName(clientObj),
    clientEmail: () => getRecipientEmail(clientObj),
    clientBusinessName: () => getRecipientBusinessName(clientObj),
    clientFirstName: () => getRecipientFirstName(clientObj),
    clientMiddleName: () => getRecipientMiddleName(clientObj),
    clientLastName: () => getRecipientLastName(clientObj),
    clientPhone: () => getRecipientPhone(clientObj),
    clientPosition: () => getRecipientPosition(clientObj),
    clientWebsite: () => getRecipientWebsite(clientObj),
    clientKvk: () => getRecipientKvk(clientObj),
    clientUniqueNumber: () => getRecipientUniqueNumber(clientObj),
    clientDateOfBirth: () => getRecipientDateOfBirth(clientObj),
    clientSex: () => getRecipientSex(clientObj),
    clientBsn: () => getRecipientBsn(clientObj),
    clientMaritalStatus: () => getRecipientMaritalStatus(clientObj),
    clientAddress: () => getRecipientAddress(clientObj),
    clientPostcode: () => getRecipientPostcode(clientObj),
    clientPlace: () => getRecipientPlace(clientObj),
    clientCountry: () => getRecipientCountry(clientObj),
    
    // Sender / Other tags
    fromName: () => getFromName(from || user),
    fromEmail: () => getFromEmail(from || user),
    fromFirstName: () => getFromFirstName(from || user),
    fromMiddleName: () => getFromMiddleName(from || user),
    fromLastName: () => getFromLastName(from || user),
    fromPhone: () => getFromPhone(from || user),
    fromPosition: () => getFromPosition(from || user),
    fromWebsite: () => getFromWebsite(from || user),
    fromKvk: () => getFromKvk(from || user),
    fromUniqueNumber: () => getFromUniqueNumber(from || user),
    fromAddress: () => getFromAddress(from || user),
    fromPostcode: () => getFromPostcode(from || user),
    fromPlace: () => getFromPlace(from || user),
    fromCountry: () => getFromCountry(from || user),
    
    // Lawyer tags (from officeMembers)
    responsibleLawyerName: () => getRecipientName(responsibleLawyer),
    responsibleLawyerEmail: () => getRecipientEmail(responsibleLawyer),
    responsibleLawyerAddress: () => getRecipientAddress(responsibleLawyer),
    treatingLawyerName: () => getRecipientName(treatingLawyer),
    treatingLawyerEmail: () => getRecipientEmail(treatingLawyer),
    treatingLawyerAddress: () => getRecipientAddress(treatingLawyer),

    // Parties (Other) tags
    opposingPartyName: () => getRecipientName(opposingParty),
    opposingPartyEmail: () => getRecipientEmail(opposingParty),
    opposingPartyAddress: () => getRecipientAddress(opposingParty),
    payingPartyName: () => getRecipientName(payingParty),
    payingPartyEmail: () => getRecipientEmail(payingParty),
    payingPartyAddress: () => getRecipientAddress(payingParty),
    advocateOpposingPartyName: () => getRecipientName(advocateOpposingParty),
    advocateOpposingPartyEmail: () => getRecipientEmail(advocateOpposingParty),
    advocateOpposingPartyAddress: () => getRecipientAddress(advocateOpposingParty),
    
    // Date tags
    currentDate: () => getCurrentDate(),
    formattedDate: () => getCurrentDate(),
  };

  // Execute handler directly - no closure issues
  if (handlerMap.hasOwnProperty(normalizedTagName)) {
    return handlerMap[normalizedTagName]();
  }

  return '';
};

/**
 * Replace all tags in content with actual values
 * @param {string} content - HTML content with {{tagName}} placeholders
 * @param {object} context - Context object with recipient, selectedCase, from, user, etc.
 * @param {Array} availableTags - Array of tag definitions from database (optional, for custom tags)
 * @returns {string} - Content with tags replaced
 */
export const replaceTags = (content, context = {}, availableTags = []) => {
  if (!content || typeof content !== 'string') {
    console.warn('replaceTags: Content is not a string', typeof content, content);
    return content || '';
  }

  // Extract all unique tags from content
  const tagRegex = /\{\{(\w+)\}\}/g;
  const foundTags = new Set();
  let match;
  
  while ((match = tagRegex.exec(content)) !== null) {
    foundTags.add(match[1]);
  }

  if (foundTags.size === 0) {
    console.log('replaceTags: No tags found in content');
    return content;
  }

  // Replace each tag with its value
  let replacedContent = content;
  
  foundTags.forEach((tagName) => {
    const value = getTagValue(tagName, context, availableTags);
    const regex = new RegExp(`\\{\\{${tagName}\\}\\}`, 'g');
    replacedContent = replacedContent.replace(regex, value || '');
  });

  return replacedContent;
};

/**
 * Extract all tags from content
 * @param {string} content - HTML content with {{tagName}} placeholders
 * @returns {Array<string>} - Array of unique tag names found
 */
export const extractTags = (content) => {
  if (!content || typeof content !== 'string') {
    return [];
  }

  const tagRegex = /\{\{(\w+)\}\}/g;
  const tags = [];
  let match;

  while ((match = tagRegex.exec(content)) !== null) {
    if (!tags.includes(match[1])) {
      tags.push(match[1]);
    }
  }

  return tags;
};

/**
 * Validate if all tags in content exist in available tags list
 * @param {string} content - HTML content with {{tagName}} placeholders
 * @param {Array<object>} availableTags - Array of available tag objects with 'name' property
 * @returns {object} - { valid: boolean, invalidTags: Array<string> }
 */
export const validateTags = (content, availableTags = []) => {
  const contentTags = extractTags(content);
  const availableTagNames = availableTags.map(tag => tag.name || tag);
  const invalidTags = contentTags.filter(tag => !availableTagNames.includes(tag));

  return {
    valid: invalidTags.length === 0,
    invalidTags,
    usedTags: contentTags,
  };
};

export default {
  replaceTags,
  extractTags,
  validateTags,
};
